/**
 * Comparison Context
 *
 * Manages items selected for comparison.
 * Persists to localStorage for cross-session retention.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const ComparisonContext = createContext(null);

// Storage key
const COMPARISON_STORAGE_KEY = 'mhf_comparison_items';

// Maximum items that can be compared
const MAX_COMPARISON_ITEMS = 10;

export function ComparisonProvider({ children }) {
  const [items, setItems] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);

  /**
   * Load comparison items from localStorage on mount
   */
  useEffect(() => {
    try {
      const stored = localStorage.getItem(COMPARISON_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          setItems(parsed);
        }
      }
    } catch (err) {
      console.error('Error loading comparison items:', err);
    }
    setIsInitialized(true);
  }, []);

  /**
   * Save comparison items to localStorage whenever they change
   */
  useEffect(() => {
    if (isInitialized) {
      try {
        localStorage.setItem(COMPARISON_STORAGE_KEY, JSON.stringify(items));
      } catch (err) {
        console.error('Error saving comparison items:', err);
      }
    }
  }, [items, isInitialized]);

  /**
   * Add an item to comparison
   */
  const addItem = useCallback((item) => {
    if (!item || !item.uuid) {
      console.error('Invalid item: must have uuid');
      return false;
    }

    setItems(prevItems => {
      // Check if already in comparison
      if (prevItems.some(i => i.uuid === item.uuid)) {
        return prevItems;
      }

      // Check max limit
      if (prevItems.length >= MAX_COMPARISON_ITEMS) {
        console.warn(`Maximum ${MAX_COMPARISON_ITEMS} items can be compared`);
        return prevItems;
      }

      // Track comparison event
      trackComparisonAdd(item.uuid);

      return [...prevItems, {
        uuid: item.uuid,
        name: item.name,
        brand: item.brand,
        price: item.price,
        image: item.images?.[0] || null,
        addedAt: Date.now()
      }];
    });

    return true;
  }, []);

  /**
   * Remove an item from comparison
   */
  const removeItem = useCallback((uuid) => {
    setItems(prevItems => prevItems.filter(item => item.uuid !== uuid));
  }, []);

  /**
   * Check if an item is in comparison
   */
  const isInComparison = useCallback((uuid) => {
    return items.some(item => item.uuid === uuid);
  }, [items]);

  /**
   * Toggle an item in comparison
   */
  const toggleItem = useCallback((item) => {
    if (isInComparison(item.uuid)) {
      removeItem(item.uuid);
      return false;
    } else {
      return addItem(item);
    }
  }, [isInComparison, addItem, removeItem]);

  /**
   * Clear all comparison items
   */
  const clearAll = useCallback(() => {
    setItems([]);
  }, []);

  /**
   * Get comparison item UUIDs as comma-separated string (for URL)
   */
  const getComparisonIds = useCallback(() => {
    return items.map(item => item.uuid).join(',');
  }, [items]);

  /**
   * Move item in comparison order
   */
  const moveItem = useCallback((uuid, direction) => {
    setItems(prevItems => {
      const index = prevItems.findIndex(item => item.uuid === uuid);
      if (index === -1) return prevItems;

      const newIndex = direction === 'left' ? index - 1 : index + 1;
      if (newIndex < 0 || newIndex >= prevItems.length) return prevItems;

      const newItems = [...prevItems];
      [newItems[index], newItems[newIndex]] = [newItems[newIndex], newItems[index]];
      return newItems;
    });
  }, []);

  const value = {
    items,
    count: items.length,
    maxItems: MAX_COMPARISON_ITEMS,
    isInitialized,
    addItem,
    removeItem,
    isInComparison,
    toggleItem,
    clearAll,
    getComparisonIds,
    moveItem,
    canAddMore: items.length < MAX_COMPARISON_ITEMS
  };

  return (
    <ComparisonContext.Provider value={value}>
      {children}
    </ComparisonContext.Provider>
  );
}

/**
 * Track comparison add event (fire-and-forget)
 */
function trackComparisonAdd(uuid) {
  try {
    fetch(`/api/items/${uuid}/track-comparison`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    }).catch(() => {
      // Ignore tracking errors
    });
  } catch {
    // Ignore
  }
}

export function useComparison() {
  const context = useContext(ComparisonContext);
  if (!context) {
    throw new Error('useComparison must be used within a ComparisonProvider');
  }
  return context;
}

export default ComparisonContext;
